import { useCallback, useEffect, useState } from "react";
import { errorIdOf, orderIssues, serverIssuesOf, type FieldIssue, type ServerIssues } from "./formErrors";
import type { ErrorSummaryProps } from "./ErrorSummary";
import { afterPointerRelease, installPointerRelease } from "./pointerRelease";

export interface UseFormErrorsOptions<V> {
  /** The form's current values; client issues are recomputed from them on every render, so they are never stale. */
  values: V;
  /** Every client-side problem with these values, one `FieldIssue` per broken field. Pure. */
  validate?: (values: V) => readonly FieldIssue[];
  /** Field keys top to bottom as the form shows them; the summary lists issues in this order. */
  order?: readonly string[];
  /** The DOM id of a field's control, the summary link's target. Default: the key itself. */
  idOf?: (field: string) => string;
  /**
   * A field's current value, used to drop a server error as soon as that field is edited. Default: `values[field]`.
   * A server error for a field this returns `undefined` for stays until the next submit.
   */
  fieldValue?: (values: V, field: string) => unknown;
}

export interface ControlProps {
  id: string;
  "aria-invalid": true | undefined;
  "aria-describedby": string | undefined;
}

export interface FormErrors<V = unknown> {
  /** True once the person has tried to submit: client issues show from then on (no red before the first try). */
  submitted: boolean;
  /** The issues to show now, one per field, in on-screen order. */
  issues: FieldIssue[];
  /** Summary-only lines (unmet rules, server errors for fields this form does not show). */
  general: string[];
  /** The message of a failed save that is not field-shaped ("Changed by another user…"), shown above the list. */
  lead: string | null;
  errorOf: (field: string) => string | undefined;
  /** The control's id, `aria-invalid` and `aria-describedby` (its inline message first, then `extraDescribedBy`). */
  controlProps: (field: string, extraDescribedBy?: string) => ControlProps;
  /** The inline message's id for a field. */
  errorIdFor: (field: string) => string;
  idOf: (field: string) => string;
  /**
   * A submit attempt: shows the client issues from now on, forgets the previous save's server errors, and moves focus
   * to the summary when anything is wrong. Returns true when the values are valid and the caller may save. `current`
   * checks values read at submit time instead of the rendered ones (an uncontrolled form, e.g. a password manager's
   * autofill that fired no change event).
   */
  attempt(current?: V): boolean;
  /** Show a failed save: its field errors on their fields, the rest in the summary, `lead` above. Focuses the summary. */
  fail: (caught: unknown, options?: { lead?: string | null; mapField?: (serverField: string) => string | null | undefined }) => void;
  /** Show issues the caller worked out itself (e.g. a step-specific check). Focuses the summary. */
  setServer: (issues: ServerIssues, lead?: string | null) => void;
  /** Forget the last failed save's lines (e.g. the person started over on the field it was about). */
  clearServer: () => void;
  /**
   * Mark one field as visited, so its client issue shows before the first submit (optional blur validation). During a
   * pointer press it waits for the press to end, so a message never moves the button being pressed (D-R17-04).
   */
  touch: (field: string) => void;
  /** Forget everything (e.g. the form was reset to new values). */
  reset: () => void;
  /** Props for `<ErrorSummary {...form.summary} />`. */
  summary: ErrorSummaryProps;
}

interface ServerState {
  /** Each field issue with the field's value when the server refused it (JSON), to tell when it has been edited. */
  fields: { issue: FieldIssue; snapshot: string }[];
  general: string[];
  lead: string | null;
  /**
   * Every value of the form when the server refused it (JSON). The summary-only lines (`general`, `lead`) are about that
   * attempt as a whole, so they go at the first edit of any field (D-R17-01). Null: the values could not be compared
   * (not JSON-able), and the lines stay until the next submit, as before. `PENDING`: taken at the next render — a failure
   * reported from an async handler sees the values of the render that started it, which may be older than the form's
   * own updates made while it ran (e.g. a waiver recorded before the refusal), and those are not the person's edits.
   */
  valuesSnapshot: string | null | typeof PENDING;
}

const PENDING = Symbol("snapshot at the next render");
const EMPTY: ServerState = { fields: [], general: [], lead: null, valuesSnapshot: null };
const snapshotOf = (value: unknown) => JSON.stringify(value === undefined ? null : value);
const safeSnapshotOf = (value: unknown): string | null => {
  try {
    return snapshotOf(value);
  } catch {
    return null;
  }
};
const defaultValueOf = (values: unknown, field: string) => (values !== null && typeof values === "object" ? (values as Record<string, unknown>)[field] : undefined);

/** State for the shared form-error pattern (see `formErrors.ts`). */
export function useFormErrors<V>({ values, validate, order, idOf = (field) => field, fieldValue = defaultValueOf }: UseFormErrorsOptions<V>): FormErrors<V> {
  const [submitted, setSubmitted] = useState(false);
  const [touched, setTouched] = useState<ReadonlySet<string>>(() => new Set());
  const [server, setServerState] = useState<ServerState>(EMPTY);
  const [focusSignal, setFocusSignal] = useState(0);

  const client = validate ? validate(values) : [];
  const shownClient = submitted ? client : client.filter((issue) => touched.has(issue.field));
  // A server error is about the value the server saw; once the field changes it no longer applies.
  const liveServer = server.fields.filter((entry) => snapshotOf(fieldValue(values, entry.issue.field)) === entry.snapshot).map((entry) => entry.issue);
  // The server's words for a field come first: they are about the value it actually refused, and more specific than the
  // client's generic check (e.g. "TBMS-827 is not ready yet: give a reason" over "give a reason"). Edit it and they go.
  const issues = orderIssues([...liveServer, ...shownClient], order);
  // The failed save's own words and its summary-only lines are about the values it sent: once any field is edited they
  // describe an attempt that no longer exists, so they go (the next submit says what is still wrong).
  const valuesNow = server.lead !== null || server.general.length > 0 ? safeSnapshotOf(values) : null;
  if (server.valuesSnapshot === PENDING) setServerState((current) => (current.valuesSnapshot === PENDING ? { ...current, valuesSnapshot: valuesNow } : current));
  const attemptStillShown = server.valuesSnapshot === null || server.valuesSnapshot === PENDING || valuesNow === server.valuesSnapshot;
  const general = attemptStillShown ? server.general : [];
  const lead = attemptStillShown ? server.lead : null;
  const byField = new Map(issues.map((issue) => [issue.field, issue.message]));

  const errorOf = (field: string) => byField.get(field);
  const errorIdFor = (field: string) => errorIdOf(idOf(field));
  const controlProps = (field: string, extraDescribedBy?: string): ControlProps => {
    const invalid = byField.has(field);
    return {
      id: idOf(field),
      "aria-invalid": invalid ? true : undefined,
      "aria-describedby": [invalid ? errorIdFor(field) : "", extraDescribedBy ?? ""].filter(Boolean).join(" ") || undefined,
    };
  };

  const attempt = (current?: V) => {
    setSubmitted(true);
    setServerState(EMPTY);
    const valid = (current !== undefined && validate ? validate(current) : client).length === 0;
    if (!valid) setFocusSignal((n) => n + 1);
    return valid;
  };

  const setServer = (found: ServerIssues, lead: string | null = null) => {
    setServerState({
      fields: found.fieldIssues.map((issue) => ({ issue, snapshot: snapshotOf(fieldValue(values, issue.field)) })),
      general: found.general,
      lead,
      valuesSnapshot: PENDING,
    });
    if (found.fieldIssues.length > 0 || found.general.length > 0 || lead) setFocusSignal((n) => n + 1);
  };

  const fail: FormErrors["fail"] = (caught, options = {}) => setServer(serverIssuesOf(caught, options.mapField), options.lead ?? null);

  // Listen for presses from the first render, so a press that starts before any blur is seen.
  useEffect(installPointerRelease, []);
  // A blur caused by pressing a button shows its message only after the press (D-R17-04): see pointerRelease.ts.
  const touch = useCallback(
    (field: string) => afterPointerRelease(() => setTouched((current) => (current.has(field) ? current : new Set([...current, field])))),
    [],
  );

  const clearServer = () => setServerState(EMPTY);

  const reset = () => {
    setSubmitted(false);
    setTouched(new Set());
    setServerState(EMPTY);
  };

  return {
    submitted,
    issues,
    general,
    lead,
    errorOf,
    controlProps,
    errorIdFor,
    idOf,
    attempt,
    fail,
    setServer,
    clearServer,
    touch,
    reset,
    // Before the first submit the summary lists only what a save refused; a visited field's own error shows inline.
    summary: { issues: submitted ? issues : orderIssues(liveServer, order), general, lead, idOf, focusSignal },
  };
}
